import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

interface Article {
  id: number;
  title: string;
  content: string;
  image: string | null;
  created_at: string;
  category: string;
  views: number;
  status: string;
  summary: string;
}

const ArticleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    
    fetch(`https://fellfell.ir/api/articles.php?action=single&id=${id}`, {
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => {
        console.log('📦 پاسخ مقاله:', data);
        if (data.success && data.data) {
          setArticle(data.data);
        } else {
          setError(data.message || 'مقاله یافت نشد');
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('❌ خطا:', err);
        setError('خطا در بارگذاری مقاله');
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        ⏳ در حال بارگذاری مقاله...
      </div>
    );
  }

  if (error || !article) {
    return (
      <div style={{ padding: '20px', color: '#c62828', background: '#ffebee', borderRadius: '10px' }}>
        ❌ {error || 'مقاله یافت نشد'}
      </div>
    );
  }

  return (
    <>
      <div style={{ background: '#1e3a1e', color: '#fff', padding: '40px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <h1 style={{ fontSize: '28px' }}>{article.title}</h1>
        </div>
      </div>
      
      <section style={{ padding: '30px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          {article.image && (
            <img 
              src={`https://fellfell.ir/uploads/articles/${article.image}`} 
              alt={article.title} 
              style={{ width: '100%', borderRadius: '12px', marginBottom: '20px', maxHeight: '400px', objectFit: 'cover' }}
              onError={(e) => { (e.target as HTMLImageElement).src = '/images/default-article.jpg'; }}
            />
          )}
          
          <div style={{ display: 'flex', gap: '20px', color: '#666', marginBottom: '20px', flexWrap: 'wrap' }}>
            <span>📂 دسته: {article.category || 'عمومی'}</span>
            <span>📅 تاریخ: {new Date(article.created_at).toLocaleDateString('fa-IR')}</span>
            <span>👁️ بازدید: {article.views || 0}</span>
            <span>
              وضعیت: {article.status === 'published' ? '✅ منتشر شده' : '📝 پیش‌نویس'}
            </span>
          </div>
          
          {article.summary && (
            <div style={{ 
              background: '#f5faf5', 
              padding: '15px 20px', 
              borderRadius: '10px', 
              marginBottom: '20px',
              borderRight: '4px solid #2e7d32'
            }}>
              <strong>📌 خلاصه:</strong> {article.summary}
            </div>
          )}
          
          {/* ── نمایش محتوای HTML ────────────────────────── */}
          <div 
            className="article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
            style={{ lineHeight: '1.9', fontSize: '16px' }}
          />
          
          {/* ── استایل‌های پایه برای محتوای مقاله ────────── */}
          <style>{`
            .article-content {
              color: #333;
              direction: rtl;
              text-align: justify;
            }
            .article-content h1,
            .article-content h2,
            .article-content h3,
            .article-content h4,
            .article-content h5,
            .article-content h6 {
              color: #1e3a1e;
              margin-top: 1.5em;
              margin-bottom: 0.5em;
            }
            .article-content h1 { font-size: 2em; }
            .article-content h2 { font-size: 1.7em; }
            .article-content h3 { font-size: 1.4em; }
            .article-content p {
              margin-bottom: 1em;
              line-height: 1.9;
            }
            .article-content ul,
            .article-content ol {
              padding-right: 1.5em;
              margin-bottom: 1em;
            }
            .article-content li {
              margin-bottom: 0.3em;
            }
            .article-content img {
              max-width: 100%;
              height: auto;
              border-radius: 8px;
              margin: 1em 0;
            }
            .article-content blockquote {
              border-right: 4px solid #2e7d32;
              background: #f5faf5;
              padding: 15px 20px;
              margin: 1em 0;
              border-radius: 8px;
              color: #1e3a1e;
            }
            .article-content table {
              width: 100%;
              border-collapse: collapse;
              margin: 1em 0;
            }
            .article-content table th,
            .article-content table td {
              border: 1px solid #ddd;
              padding: 8px 12px;
              text-align: right;
            }
            .article-content table th {
              background: #e8f5e9;
              color: #1e3a1e;
            }
            .article-content a {
              color: #2e7d32;
              text-decoration: underline;
            }
            .article-content a:hover {
              color: #1b5e20;
            }
            .article-content pre {
              background: #f5f5f5;
              padding: 15px;
              border-radius: 8px;
              overflow-x: auto;
              font-family: 'Courier New', monospace;
              font-size: 14px;
              direction: ltr;
              text-align: left;
            }
            .article-content code {
              background: #f5f5f5;
              padding: 2px 6px;
              border-radius: 4px;
              font-family: 'Courier New', monospace;
              font-size: 14px;
            }
          `}</style>
        </div>
      </section>
    </>
  );
};

export default ArticleDetail;